<template>
	<div class="order-detail-page pb-12">
		<v-container v-if="pedido">
			<!-- Breadcrumb Navigation -->
			<div class="d-flex align-center py-3 mb-2 text-caption text-secondary-custom">
				<router-link to="/" class="text-secondary-custom text-decoration-none hover-underline">
					{{ "store" | trans }}
				</router-link>
				<v-icon x-small class="mx-2">mdi-chevron-right</v-icon>
				<router-link to="/profile" class="text-secondary-custom text-decoration-none hover-underline">
					{{ "orders" | trans }}
				</router-link>
				<v-icon x-small class="mx-2">mdi-chevron-right</v-icon>
				<span class="text-slate-800 font-weight-medium"> {{ "order" | trans }} #{{ pedido.numero_pedido }} </span>
			</div>

			<!-- Order Header Card -->
			<v-card class="order-header-card pa-6 mb-6 rounded-xl" flat outlined>
				<div class="d-flex align-center justify-space-between flex-wrap">
					<div class="d-flex align-center">
						<v-avatar size="44" color="#eff6ff" class="mr-3">
							<v-icon color="primary">mdi-package-variant-closed</v-icon>
						</v-avatar>
						<div>
							<h1 class="text-h6 font-weight-bold text-slate-900 mb-1">
								{{ "order" | trans }} #{{ pedido.numero_pedido }}
							</h1>
							<div class="caption text-secondary-custom">
								{{ "client" | trans }}:
								<strong class="text-slate-800">{{ pedido.cliente ? pedido.cliente.full_name : "" }}</strong> &bull;
								{{ pedido.created_at | moment("calendar") }}
							</div>
						</div>
					</div>

					<div class="mt-3 mt-sm-0">
						<span
							class="status-pill caption font-weight-bold px-3 py-1 rounded-pill"
							:class="`${pedido.estado}`.toLowerCase().includes('pagado') ? 'status-paid' : 'status-pending'"
						>
							{{ pedido.estado }}
						</span>
					</div>
				</div>
			</v-card>

			<v-row>
				<!-- Order Items Details -->
				<v-col cols="12" lg="8">
					<v-card class="order-items-card pa-6 rounded-xl" flat outlined>
						<h3 class="text-subtitle-1 font-weight-bold text-slate-900 mb-4">
							Artículos del Pedido ({{ pedido.items ? pedido.items.length : 0 }})
						</h3>

						<div v-if="pedido.items && pedido.items.length > 0">
							<div
								v-for="prod in pedido.items"
								:key="prod.id"
								class="d-flex align-center justify-space-between py-3 border-b"
							>
								<div class="d-flex align-center">
									<v-avatar size="40" color="#f8fafc" class="mr-3 rounded-lg border">
										<v-icon small color="#94a3b8">mdi-cube-outline</v-icon>
									</v-avatar>
									<div>
										<h4 class="caption font-weight-bold text-slate-800 mb-1">{{ prod.name }}</h4>
										<div class="caption text-secondary-custom">
											{{ prod.cantidad_pedidos }} x {{ prod.precio | currency("$", 0) }}
										</div>
									</div>
								</div>

								<div class="caption font-weight-bold text-slate-900">
									{{ (prod.cantidad_pedidos * prod.precio) | currency("$", 0) }}
								</div>
							</div>
						</div>
					</v-card>
				</v-col>

				<!-- Summary & Payment Column -->
				<v-col cols="12" lg="4">
					<v-card class="order-summary-card pa-6 rounded-xl" flat outlined>
						<h3 class="text-subtitle-1 font-weight-bold text-slate-900 mb-4">Resumen</h3>

						<div class="d-flex justify-space-between align-center mb-6">
							<span class="text-subtitle-1 font-weight-bold text-slate-900">{{ "total" | trans }}</span>
							<span class="text-h6 font-weight-bold text-primary">{{ pedido.total | currency("$", 0) }}</span>
						</div>

						<!-- Payment Options if Pending -->
						<div
							v-if="
								pedido.total > 0 &&
								(pedido.estado == 'Pendiente de Pago' ||
									pedido.estado == 'no pagado' ||
									pedido.estado == 'Error en el Pago')
							"
						>
							<v-divider class="my-4"></v-divider>
							<p class="caption font-weight-bold text-slate-800 mb-3">Opciones de Pago:</p>

							<div v-if="api.user && api.user.can_pay_with_payu" class="mb-2">
								<v-btn
									depressed
									block
									large
									color="#A6C307"
									class="white--text rounded-pill text-capitalize"
									@click="payment(pedido.id, 'payu')"
								>
									Pagar con PayU
								</v-btn>
							</div>

							<div v-if="api.user && api.user.can_pay_with_place_to_pay" class="mb-2">
								<v-btn
									depressed
									block
									large
									color="#FF6600"
									class="white--text rounded-pill text-capitalize"
									@click="payment(pedido.id, 'PLACE_TO_PAY')"
								>
									Pagar con Place to Pay
								</v-btn>
							</div>

							<div v-if="api.user && api.user.can_pay_with_bold" class="mb-2">
								<v-btn
									depressed
									block
									large
									color="#1A1053"
									class="white--text rounded-pill text-capitalize"
									@click="payment(pedido.id, 'BOLD')"
								>
									<v-icon color="white" small class="mr-1">mdi-credit-card-fast-outline</v-icon>
									Pagar con BOLD
								</v-btn>
							</div>

							<div v-if="api.user && api.user.can_pay_with_mercado_pago" class="mb-2">
								<v-btn
									depressed
									block
									large
									color="#009EE3"
									class="white--text rounded-pill text-capitalize"
									@click="payment(pedido.id, 'MERCADO_PAGO')"
								>
									<v-icon color="white" small class="mr-1">mdi-credit-card-outline</v-icon>
									Pagar con Mercado Pago
								</v-btn>
							</div>

							<div v-if="api.user && api.user.can_pay_with_stripe" class="mb-2">
								<v-btn
									depressed
									block
									large
									color="#635BFF"
									class="white--text rounded-pill text-capitalize"
									@click="payment(pedido.id, 'STRIPE')"
								>
									Pagar con Stripe
								</v-btn>
							</div>
						</div>

						<div class="mt-6 text-center">
							<v-btn text small color="primary" to="/profile" class="text-capitalize">
								<v-icon left small>mdi-arrow-left</v-icon>
								Volver a mis pedidos
							</v-btn>
						</div>
					</v-card>
				</v-col>
			</v-row>
		</v-container>

		<v-container v-else class="text-center py-16">
			<v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
			<p class="mt-4 text-secondary-custom">{{ "store.loading" | trans }}</p>
		</v-container>
	</div>
</template>

<script>
export default {
	name: "Order",
	props: {
		orderId: {
			type: [Number, String],
			required: true,
		},
	},
	data() {
		return {
			pedido: null,
		};
	},
	mounted() {
		this.getData();
	},
	methods: {
		async getData() {
			const orderId = this.orderId;
			const pedido = (await this.api.get(`pedidos/${orderId}?include=cliente,items`)).data;
			this.pedido = pedido;
		},
		payment(pedId, method = "payu") {
			this.api.payment(pedId, method);
		},
	},
};
</script>

<style scoped>
.order-detail-page {
	background-color: #f8fafc;
	min-height: 100vh;
}

.order-header-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
}

.order-items-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
}

.order-summary-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
}

.status-paid {
	background-color: #ecfdf5;
	color: #059669;
}

.status-pending {
	background-color: #fffbeb;
	color: #d97706;
}

.border-b {
	border-bottom: 1px solid #f1f5f9;
}

.border {
	border: 1px solid #e2e8f0;
}

.text-slate-900 {
	color: #0f172a;
}

.text-slate-800 {
	color: #1e293b;
}

.text-secondary-custom {
	color: #64748b;
}

.hover-underline:hover {
	text-decoration: underline;
}
</style>
